// --- Our Modules --------------------------------------------------------------------------------
import constants from "@/config/constants";
import usePermission from "@/hooks/usePermission";
import usePopup from "@/hooks/usePopup";
import useTranslation from "@/hooks/useTranslation";
import type { TyLead, TyLeadStage } from "@/types";
import ConfirmPopup from "@/UI/popups/ConfirmPopup";
import "../stepper.css";
import "./styles.css";
// --- Logic --------------------------------------------------------------------------------------
type Props = {
    lead: TyLead;
    /** Moves the deal to that stage. Absent makes the trail a picture rather than a control. */
    onPick?: (stage: TyLeadStage) => void;
    disabled?: boolean;
};

/** The happy path, in order. `lost` is not on it — it is where a deal leaves the path, not a step. */
const FLOW = [...constants.leadWorkingStages, "won"] as readonly TyLeadStage[];

/**
 * Where a deal has got to, as the trail it actually walks — the same shape the order status takes in
 * our other product, because it is the same question asked of a different noun.
 *
 * It replaces a chip that said one word and a popup that asked which word to say next. A chip can
 * report a stage but never show the distance left, and the distance is the thing a sales manager
 * reads a pipeline for.
 *
 * **What it will not do is make an illegal move look possible.** A member's pipeline runs forward
 * only and a loss needs a reason, so earlier steps are dead for a member and `lost` is left to the
 * actions menu where it can ask why. A step that is not a legal move is not clickable.
 *
 * **`won` is clickable but is not a stage change.** It is in `LEAD_SYSTEM_STAGES` and the stage
 * route refuses it: the only two things that write it are a reservation and a contract. So pressing
 * it hands `"won"` to the caller, which opens the form asking which of those two happened — and it
 * skips the confirmation, because that form is a much better one.
 *
 * A lost deal has no path left to draw, so it says so instead of showing a half-filled trail.
 *
 * **Every move is confirmed, and the confirmation lives here** rather than in the two screens that
 * draw one. A stage is the number a whole pipeline is reported on, the control sits on a card in a
 * grid where a mis-aimed tap is one pixel away, and the move is not reversible for a member — so
 * the dialog is part of the control, not something each caller remembers to wrap it in.
 */
export default function LeadStepper({ lead, onPick, disabled }: Props) {
    const t = useTranslation();
    const { isOwner, role } = usePermission();
    const addComp = usePopup((state) => state.addComp);
    const canMoveFreely = isOwner || role === "manager";

    const confirm = (stage: TyLeadStage) =>
        addComp({
            name: "confirm",
            Component: ConfirmPopup,
            anim: "scale",
            details: {
                title: t.crm.moveStage,
                message: t.crm.confirmStage.replace("{stage}", t.crm.stages[stage]),
                confirmLabel: t.crm.moveStage,
                danger: false,
                onConfirm: () => onPick?.(stage),
            },
        });

    if (lead.stage === "lost") {
        return (
            <div className="lead-stepper--off">
                <span className="lead-stepper__off-dot" aria-hidden="true" />
                <span className="lead-stepper__off-label">
                    {t.crm.stages.lost}
                    {lead.lostReason ? ` · ${t.crm.lostReasons[lead.lostReason]}` : ""}
                </span>
            </div>
        );
    }

    const at = FLOW.indexOf(lead.stage);

    return (
        <div className="stepper">
            {FLOW.map((step, index) => {
                const state = index < at ? "done" : index === at ? "now" : "next";
                const isSystem = (constants.leadSystemStages as readonly string[]).includes(step);
                const isLegal = !!onPick && !disabled && index !== at && !lead.closed && (canMoveFreely || index > at);

                return (
                    <button
                        key={step}
                        type="button"
                        className="stepper__step"
                        data-state={state}
                        disabled={!isLegal}
                        onClick={(event) => {
                            // The stepper sits on cards that are themselves links to the record.
                            event.stopPropagation();
                            // A system stage opens a form of its own, which asks more than a yes.
                            if (isSystem) onPick?.(step);
                            else confirm(step);
                        }}
                    >
                        <span className="stepper__dot" aria-hidden="true" />
                        <span className="stepper__label">{t.crm.stages[step]}</span>
                    </button>
                );
            })}
        </div>
    );
}
